// src/components/ClientPortalCases.js

import React, { useState, useCallback } from 'react';
import 'animate.css';

function ClientPortalCases({ currentUser, clients, cases }) {
  // Find the specific client profile linked to the current user
  const clientProfile = clients.find(client => client.user === currentUser.id);

  // Filter cases relevant to this specific client profile
  const clientCases = cases.filter(c => c.client === (clientProfile ? clientProfile.id : null));

  const [showSubCases, setShowSubCases] = useState({}); // State to manage visibility of sub-cases

  // Function to get sub-cases for a given parent case
  const getSubCases = useCallback((parentId) => {
    return clientCases.filter(c => c.parent_case === parentId);
  }, [clientCases]);

  // Toggle sub-case visibility
  const toggleSubCases = useCallback((caseId) => {
    setShowSubCases(prev => ({
      ...prev,
      [caseId]: !prev[caseId]
    }));
  }, []);

  return (
    <div className="client-portal-cases animate__animated animate__fadeInUp">
      <h2 className="mb-4 text-primary">Your Cases</h2>
      {clientProfile ? (
        <p className="lead text-secondary">View all cases associated with your profile, {clientProfile.first_name} {clientProfile.last_name}.</p>
      ) : (
        <p className="lead text-warning">Your user account is not yet linked to a client profile. Please contact the firm administrator to view your cases.</p>
      )}

      {clientCases.length > 0 ? (
        <div className="table-responsive">
          <table className="table table-hover table-striped shadow-sm rounded overflow-hidden">
            <thead className="bg-primary text-white">
              <tr>
                <th>Title</th>
                <th>Type</th>
                <th>Status</th>
                <th>Phase</th>
                <th>Opening Date</th>
                <th>Assigned Attorney</th>
                <th>Court Info</th>
              </tr>
            </thead>
            <tbody>
              {clientCases.filter(c => !c.parent_case).map(_case => ( // Filter for top-level cases
                <React.Fragment key={_case.id}>
                  <tr>
                    <td>
                      {_case.title}
                      {getSubCases(_case.id).length > 0 && (
                        <button
                          className="btn btn-sm btn-link p-0 ms-2"
                          onClick={() => toggleSubCases(_case.id)}
                          title={showSubCases[_case.id] ? 'Hide Sub-Cases' : 'Show Sub-Cases'}
                        >
                          <i className={`bi ${showSubCases[_case.id] ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                        </button>
                      )}
                    </td>
                    <td>{_case.case_type}</td>
                    <td>{_case.status}</td>
                    <td>{_case.phase}</td>
                    <td>{_case.opening_date}</td>
                    <td>{_case.assigned_attorney_username || 'N/A'}</td>
                    <td>
                      {_case.court_name && <div><strong>Court:</strong> {_case.court_name}</div>}
                      {_case.jurisdiction && <div><strong>Jurisdiction:</strong> {_case.jurisdiction}</div>}
                      {_case.court_case_number && <div><strong>Case #:</strong> {_case.court_case_number}</div>}
                      {!_case.court_name && !_case.jurisdiction && !_case.court_case_number && 'N/A'}
                    </td>
                  </tr>
                  {showSubCases[_case.id] && getSubCases(_case.id).map(subCase => (
                    <tr key={subCase.id} className="table-light animate__animated animate__fadeInLeft">
                      <td className="ps-4">
                        <i className="bi bi-arrow-return-right me-2"></i> {subCase.title}
                      </td>
                      <td>{subCase.case_type}</td>
                      <td>{subCase.status}</td>
                      <td>{subCase.phase}</td>
                      <td>{subCase.opening_date}</td>
                      <td>{subCase.assigned_attorney_username || 'N/A'}</td>
                      <td>
                        {subCase.court_name && <div><strong>Court:</strong> {subCase.court_name}</div>}
                        {subCase.jurisdiction && <div><strong>Jurisdiction:</strong> {subCase.jurisdiction}</div>}
                        {subCase.court_case_number && <div><strong>Case #:</strong> {subCase.court_case_number}</div>}
                        {!subCase.court_name && !subCase.jurisdiction && !subCase.court_case_number && 'N/A'}
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="alert alert-info mt-4" role="alert">
          No cases found associated with your client profile.
        </div>
      )}
    </div>
  );
}

export default ClientPortalCases;
